import { apiFetch } from '../providers/api-rest-provider';
import { useEffect, useState, useRef } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Create,
  Edit,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextInput,
  FunctionField,
  useRecordContext,
  useDataProvider,
  useNotify,
  BulkDeleteButton,
} from 'react-admin';
import {
  Chip,
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField as MuiTextField,
  CircularProgress,
  Stack,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ScopePicker } from '../components/ScopePicker';
import { useController } from 'react-hook-form';
import { canonicalOrgPath, isOrgPathWithin } from '../hooks/useRollup';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Certification {
  name: string;
  expiresAt: string;
  certId: string;
}

interface AvailabilityBlock {
  dayOfWeek: string;
  startTime: string;
  endTime: string;
}

interface InstructorRecord {
  id: string;
  userId: string;
  assignedOrgPath?: string;
  certifications?: Certification[];
  qualifiedAssessments?: string[];
  availabilityBlocks?: AvailabilityBlock[];
  archived_at?: string;
}

interface UtilizationRow {
  userId: string;
  assignedOrgPath?: string;
  availableHoursPerWeek: number;
  scheduledInstances: number;
  certificationWarnings: Certification[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

function hasCertWarning(certifications?: Certification[]): boolean {
  if (!certifications || certifications.length === 0) return false;
  const cutoff = Date.now() + SIXTY_DAYS_MS;
  return certifications.some((c) => new Date(c.expiresAt).getTime() <= cutoff);
}

// ---------------------------------------------------------------------------
// ScopePicker as react-hook-form field
// ---------------------------------------------------------------------------

const ScopePickerInput = ({ source, label }: { source: string; label?: string }) => {
  const { field } = useController({ name: source, defaultValue: '' });
  return (
    <ScopePicker value={field.value ?? ''} onChange={field.onChange} label={label ?? 'Org Path'} />
  );
};
ScopePickerInput.displayName = 'ScopePickerInput';

// ---------------------------------------------------------------------------
// Certifications warning chip for list view
// ---------------------------------------------------------------------------

const CertWarningChip = () => {
  const record = useRecordContext<InstructorRecord>();
  if (!record) return null;
  if (hasCertWarning(record.certifications)) {
    return (
      <Chip
        icon={<WarningIcon />}
        label="Cert expiring"
        size="small"
        color="error"
        variant="outlined"
      />
    );
  }
  return <Chip label="OK" size="small" color="success" variant="outlined" />;
};
CertWarningChip.displayName = 'CertWarningChip';

// ---------------------------------------------------------------------------
// Repeatable certifications field (simple chip list for show)
// ---------------------------------------------------------------------------

const CertificationsShowField = () => {
  const record = useRecordContext<InstructorRecord>();
  if (!record?.certifications || record.certifications.length === 0) return <Typography variant="body2" color="text.secondary">None</Typography>;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {record.certifications.map((c, i) => (
        <Chip
          key={i}
          label={`${c.name} (${c.certId}) exp: ${c.expiresAt?.slice(0, 10)}`}
          size="small"
          color={hasCertWarning([c]) ? 'error' : 'default'}
        />
      ))}
    </Stack>
  );
};
CertificationsShowField.displayName = 'CertificationsShowField';

// ---------------------------------------------------------------------------
// Repeatable certification inputs for create/edit
// ---------------------------------------------------------------------------

const CertificationsInput = () => {
  const { field } = useController({
    name: 'certifications',
    defaultValue: [] as Certification[],
  });

  const certs: Certification[] = field.value ?? [];

  const addCert = () => {
    field.onChange([...certs, { name: '', expiresAt: '', certId: '' }]);
  };

  const removeCert = (idx: number) => {
    field.onChange(certs.filter((_, i) => i !== idx));
  };

  const updateCert = (idx: number, key: keyof Certification, value: string) => {
    const updated = certs.map((c, i) => (i === idx ? { ...c, [key]: value } : c));
    field.onChange(updated);
  };

  return (
    <Box mt={1} mb={1} width="100%">
      <Typography variant="subtitle2" gutterBottom>
        Certifications
      </Typography>
      {certs.map((cert, idx) => (
        <Box key={idx} display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
          <MuiTextField
            label="Name"
            size="small"
            value={cert.name}
            onChange={(e) => updateCert(idx, 'name', e.target.value)}
          />
          <MuiTextField
            label="Cert ID"
            size="small"
            value={cert.certId}
            onChange={(e) => updateCert(idx, 'certId', e.target.value)}
          />
          <MuiTextField
            label="Expires At"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={cert.expiresAt?.slice(0, 10) ?? ''}
            onChange={(e) => updateCert(idx, 'expiresAt', e.target.value)}
          />
          <Button size="small" color="error" onClick={() => removeCert(idx)}>
            Remove
          </Button>
        </Box>
      ))}
      <Button size="small" variant="outlined" onClick={addCert}>
        + Add Certification
      </Button>
    </Box>
  );
};
CertificationsInput.displayName = 'CertificationsInput';

// ---------------------------------------------------------------------------
// Multi-value chip input for qualifiedAssessments
// ---------------------------------------------------------------------------

const QualifiedAssessmentsInput = () => {
  const { field } = useController({
    name: 'qualifiedAssessments',
    defaultValue: [] as string[],
  });
  const list: string[] = field.value ?? [];
  const [draft, setDraft] = useState('');

  const add = () => {
    const trimmed = draft.trim();
    if (trimmed && !list.includes(trimmed)) {
      field.onChange([...list, trimmed]);
    }
    setDraft('');
  };

  const remove = (val: string) => {
    field.onChange(list.filter((v) => v !== val));
  };

  return (
    <Box mt={1} mb={1} width="100%">
      <Typography variant="subtitle2" gutterBottom>
        Qualified Assessments
      </Typography>
      <Box display="flex" gap={1} mb={1} flexWrap="wrap">
        {list.map((val) => (
          <Chip key={val} label={val} size="small" onDelete={() => remove(val)} />
        ))}
      </Box>
      <Box display="flex" gap={1}>
        <MuiTextField
          size="small"
          placeholder="Assessment ID"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button size="small" variant="outlined" onClick={add}>
          Add
        </Button>
      </Box>
    </Box>
  );
};
QualifiedAssessmentsInput.displayName = 'QualifiedAssessmentsInput';

// ---------------------------------------------------------------------------
// Availability blocks input
// ---------------------------------------------------------------------------

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AvailabilityBlocksInput = () => {
  const { field } = useController({
    name: 'availabilityBlocks',
    defaultValue: [] as AvailabilityBlock[],
  });
  const blocks: AvailabilityBlock[] = field.value ?? [];

  const addBlock = () => {
    field.onChange([...blocks, { dayOfWeek: 'Monday', startTime: '08:00', endTime: '16:00' }]);
  };

  const removeBlock = (idx: number) => {
    field.onChange(blocks.filter((_, i) => i !== idx));
  };

  const updateBlock = (idx: number, key: keyof AvailabilityBlock, value: string) => {
    const updated = blocks.map((b, i) => (i === idx ? { ...b, [key]: value } : b));
    field.onChange(updated);
  };

  return (
    <Box mt={1} mb={1} width="100%">
      <Typography variant="subtitle2" gutterBottom>
        Availability Blocks
      </Typography>
      {blocks.map((block, idx) => (
        <Box key={idx} display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
          <MuiTextField
            select
            label="Day"
            size="small"
            value={block.dayOfWeek}
            onChange={(e) => updateBlock(idx, 'dayOfWeek', e.target.value)}
            SelectProps={{ native: true }}
            sx={{ minWidth: 130 }}
          >
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </MuiTextField>
          <MuiTextField
            label="Start"
            type="time"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={block.startTime}
            onChange={(e) => updateBlock(idx, 'startTime', e.target.value)}
          />
          <MuiTextField
            label="End"
            type="time"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={block.endTime}
            onChange={(e) => updateBlock(idx, 'endTime', e.target.value)}
          />
          <Button size="small" color="error" onClick={() => removeBlock(idx)}>
            Remove
          </Button>
        </Box>
      ))}
      <Button size="small" variant="outlined" onClick={addBlock}>
        + Add Block
      </Button>
    </Box>
  );
};
AvailabilityBlocksInput.displayName = 'AvailabilityBlocksInput';

// ---------------------------------------------------------------------------
// CSV Bulk Import button (inside BulkActionsToolbar)
// ---------------------------------------------------------------------------

const BulkCsvImportButton = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      if (!text) return;

      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
      if (lines.length < 2) {
        notify('CSV must have a header row and at least one data row.', { type: 'warning' });
        return;
      }

      // Expected columns: userId, assignedOrgPath, certName, certExpiresAt
      const [, ...dataLines] = lines; // skip header
      let successCount = 0;
      let errorCount = 0;

      for (const line of dataLines) {
        const cols = line.split(',').map((c) => c.trim());
        const [userId, assignedOrgPath, certName, certExpiresAt] = cols;
        if (!userId) continue;

        const record: Partial<InstructorRecord> = { userId, assignedOrgPath };
        if (certName && certExpiresAt) {
          record.certifications = [{ name: certName, expiresAt: certExpiresAt, certId: `${userId}-${certName}` }];
        }

        try {
          await dataProvider.create('instructors', { data: record });
          successCount++;
        } catch {
          errorCount++;
        }
      }

      notify(
        `CSV import complete: ${successCount} created, ${errorCount} errors.`,
        { type: errorCount > 0 ? 'warning' : 'success' },
      );
    };

    reader.readAsText(file);
    // Reset so same file can be re-imported
    e.target.value = '';
  };

  return (
    <>
      <input
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <Button
        size="small"
        startIcon={<UploadFileIcon />}
        onClick={() => fileInputRef.current?.click()}
      >
        Import CSV
      </Button>
    </>
  );
};
BulkCsvImportButton.displayName = 'BulkCsvImportButton';

const InstructorBulkActions = () => (
  <>
    <BulkDeleteButton />
    <BulkCsvImportButton />
  </>
);
InstructorBulkActions.displayName = 'InstructorBulkActions';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const InstructorList = () => (
  <List exporter={false} perPage={25}>
    <Datagrid
      rowClick="edit"
      bulkActionButtons={<InstructorBulkActions />}
    >
      <TextField source="userId" label="User ID" />
      <TextField source="assignedOrgPath" label="Org Path" />
      <FunctionField
        label="Certifications"
        render={(record: InstructorRecord) =>
          record?.certifications ? String(record.certifications.length) : '0'
        }
      />
      <CertWarningChip />
    </Datagrid>
  </List>
);
InstructorList.displayName = 'InstructorList';

// ---------------------------------------------------------------------------
// Show
// ---------------------------------------------------------------------------

export const InstructorShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="userId" label="User ID" />
      <TextField source="assignedOrgPath" label="Org Path" />
      <FunctionField
        label="Qualified Assessments"
        render={(record: InstructorRecord) =>
          record?.qualifiedAssessments?.join(', ') ?? 'None'
        }
      />
      <FunctionField
        label="Certifications"
        render={() => null}
      />
      <CertificationsShowField />
    </SimpleShowLayout>
  </Show>
);
InstructorShow.displayName = 'InstructorShow';

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

export const InstructorCreate = () => (
  <Create>
    <SimpleForm>
      <TextInput fullWidth source="userId" label="User ID" />
      <Box mb={1}>
        <ScopePickerInput source="assignedOrgPath" label="Assigned Org Path" />
      </Box>
      <QualifiedAssessmentsInput />
      <CertificationsInput />
      <AvailabilityBlocksInput />
    </SimpleForm>
  </Create>
);
InstructorCreate.displayName = 'InstructorCreate';

// ---------------------------------------------------------------------------
// Edit
// ---------------------------------------------------------------------------

export const InstructorEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm>
      <TextInput fullWidth source="userId" label="User ID" disabled />
      <Box mb={1}>
        <ScopePickerInput source="assignedOrgPath" label="Assigned Org Path" />
      </Box>
      <QualifiedAssessmentsInput />
      <CertificationsInput />
      <AvailabilityBlocksInput />
    </SimpleForm>
  </Edit>
);
InstructorEdit.displayName = 'InstructorEdit';

// ---------------------------------------------------------------------------
// Utilization page (standalone — not a react-admin resource)
// ---------------------------------------------------------------------------


interface UtilizationRequestState {
  key: string;
  rows: UtilizationRow[];
  loading: boolean;
  error: string | null;
}

export const InstructorUtilization = ({ rootOrgPath }: { rootOrgPath: string }) => (
  <ScopedInstructorUtilization key={rootOrgPath} rootOrgPath={rootOrgPath} />
);

const ScopedInstructorUtilization = ({ rootOrgPath }: { rootOrgPath: string }) => {
  const [orgPath, setOrgPath] = useState(rootOrgPath);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [request, setRequest] = useState<UtilizationRequestState | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);

  const root = canonicalOrgPath(rootOrgPath) === rootOrgPath ? rootOrgPath : null;
  const queryOrgPath = root
    && canonicalOrgPath(orgPath) === orgPath
    && isOrgPathWithin(root, orgPath)
    ? orgPath
    : null;
  const requestKey = `${root ?? ''}\u0000${queryOrgPath ?? ''}\u0000${from}\u0000${to}`;
  const currentRequest = request?.key === requestKey ? request : null;
  const rows = currentRequest?.rows ?? [];
  const loading = currentRequest?.loading ?? false;
  const error = currentRequest?.error ?? null;

  const cancelActiveRequest = () => {
    generationRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
  };

  useEffect(() => () => {
    generationRef.current += 1;
    controllerRef.current?.abort();
  }, []);

  const handleFetch = async () => {
    if (!queryOrgPath) return;
    cancelActiveRequest();
    const generation = generationRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    setRequest({ key: requestKey, rows: [], loading: true, error: null });
    try {
      const params = new URLSearchParams();
      params.set('orgPath', queryOrgPath);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const json = await apiFetch(
        `instructors/utilization?${params.toString()}`,
        { signal: controller.signal },
      ) as { instructors: UtilizationRow[] };
      if (controller.signal.aborted || generation !== generationRef.current) return;
      const scopedRows = (json.instructors ?? []).filter((row) => (
        typeof row.assignedOrgPath === 'string'
        && canonicalOrgPath(row.assignedOrgPath) === row.assignedOrgPath
        && isOrgPathWithin(queryOrgPath, row.assignedOrgPath)
      ));
      setRequest({ key: requestKey, rows: scopedRows, loading: false, error: null });
    } catch (e: unknown) {
      if (controller.signal.aborted || generation !== generationRef.current) return;
      setRequest({
        key: requestKey,
        rows: [],
        loading: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Instructor Utilization
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="flex-end">
        <MuiTextField
          label="Org Path"
          size="small"
          value={orgPath}
          onChange={(e) => { cancelActiveRequest(); setOrgPath(e.target.value); }}
          placeholder="/aetc"
          sx={{ minWidth: 200 }}
          error={orgPath.length > 0 && !queryOrgPath}
          helperText={orgPath.length > 0 && !queryOrgPath ? 'Choose the assignment root or a descendant.' : undefined}
        />
        <MuiTextField
          label="From"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => { cancelActiveRequest(); setFrom(e.target.value); }}
        />
        <MuiTextField
          label="To"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => { cancelActiveRequest(); setTo(e.target.value); }}
        />
        <Button variant="contained" onClick={handleFetch} disabled={loading || !queryOrgPath}>
          {loading ? <CircularProgress size={20} /> : 'Load'}
        </Button>
      </Box>

      {error && (
        <Typography color="error" mb={2}>
          {error}
        </Typography>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Instructor</TableCell>
              <TableCell>Org Path</TableCell>
              <TableCell>Available Hrs/Week</TableCell>
              <TableCell>Scheduled Instances</TableCell>
              <TableCell>Certification Warnings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading...' : 'No data. Run a query above.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow key={i}>
                  <TableCell>{row.userId}</TableCell>
                  <TableCell>{row.assignedOrgPath ?? '—'}</TableCell>
                  <TableCell>{row.availableHoursPerWeek}</TableCell>
                  <TableCell>{row.scheduledInstances}</TableCell>
                  <TableCell>
                    {row.certificationWarnings && row.certificationWarnings.length > 0 ? (
                      <Stack direction="row" gap={0.5} flexWrap="wrap">
                        {row.certificationWarnings.map((cw, j) => (
                          <Chip
                            key={j}
                            label={`${cw.name} exp ${cw.expiresAt?.slice(0, 10)}`}
                            size="small"
                            color="error"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Chip label="None" size="small" color="success" />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
InstructorUtilization.displayName = 'InstructorUtilization';
